<script lang="ts" setup>
import { computed } from 'vue';

import type { ITableFilterProps } from '@modules/tables/components/VTableFilter/types';
import type { ITableColumnsOptions } from '@modules/tables/types';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VDownloadReportButton from '@modules/download-report/components/VDownloadReportButton/VDownloadReportButton.vue';
import { useDownloadReport } from '@modules/download-report/composables';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { ESmartFilterColumnType } from '@modules/smart-filters/enums';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';
import {
  TRANSLATION_GROUP_ADMIN_WEBHOOKS_HISTORY,
  TRANSLATION_GROUP_AFFILIATES_WEBHOOKS_HISTORY,
  WEBHOOKS_HISTORY_PERMISSIONS,
} from '@modules/webhooks-history/constants';
import {
  useDownloadWebhooksHistoryReportMutation,
} from '@modules/webhooks-history/mutations';
import {
  useWebhooksHistoryListQuery,
} from '@modules/webhooks-history/queries';
import {
  ADMIN_WEBHOOKS_HISTORY_DETAILS_ROUTE_NAME,
  AFFILIATE_WEBHOOKS_HISTORY_DETAILS_ROUTE_NAME,
} from '@modules/webhooks-history/submodules/history-details/constants';

const { isAdminNamespace } = useCurrentNamespace();
const { getTranslation, getTPath } = useI18n();

const title = computed(() => (getTPath(isAdminNamespace.value
  ? TRANSLATION_GROUP_ADMIN_WEBHOOKS_HISTORY
  : TRANSLATION_GROUP_AFFILIATES_WEBHOOKS_HISTORY, 'HEADER')));

const label = computed(() => (getTranslation(isAdminNamespace.value
  ? TRANSLATION_GROUP_ADMIN_WEBHOOKS_HISTORY
  : TRANSLATION_GROUP_AFFILIATES_WEBHOOKS_HISTORY, 'DOWNLOAD_REPORTS').value));

const routeName = computed(() => (isAdminNamespace.value ? ADMIN_WEBHOOKS_HISTORY_DETAILS_ROUTE_NAME : AFFILIATE_WEBHOOKS_HISTORY_DETAILS_ROUTE_NAME));

const {
  data: tableData,
  isLoading,
  isFetching,
  tableState,
  updateTableState,
  refreshTableData,
} = useTableData(useWebhooksHistoryListQuery);

const {
  isReportLoading,
  onDownloadReport,
} = useDownloadReport(useDownloadWebhooksHistoryReportMutation(), tableState);

const filterOptions: ITableFilterProps = {
  pathForFilter: 'api2/:namespace/requests',
  actionForFilter: 'list',
  filterName: 'webhook_request',
  byMerchant: true,
};

const columnOptions: ITableColumnsOptions = {
  webhook_id: {
    type: ESmartFilterColumnType.COMPARE,
  },
  affiliate_id: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  updated_at: {
    type: ESmartFilterColumnType.DATE,
  },
  serial_id: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  kind: {
    type: ESmartFilterColumnType.CHECKBOX,
    defaultMobilePreview: true,
  },
  uri: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  response_code: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  params: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  email: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  username: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  merchant_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
};
</script>

<template>
  <VCard v-data-qa-auto-id="'WebhooksHistoryListPage'" type="table">
    <VTable
      :data="tableData"
      :title-t-path="title"
      :loading="isLoading"
      :is-fetching="isFetching"
      :filter-props="filterOptions"
      :columns-options="columnOptions"
      :state="tableState"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="$hasPermission(WEBHOOKS_HISTORY_PERMISSIONS.SHOW)"
          icon="pi pi-eye"
          :to="{ name: routeName, params: { id: listItem.id } }"
        />
      </template>
      <template
        v-if="$hasPermission(WEBHOOKS_HISTORY_PERMISSIONS.DOWNLOAD)"
        #footerButtons
      >
        <VDownloadReportButton
          :label="label"
          :loading="isReportLoading"
          @download="onDownloadReport"
        />
      </template>
    </VTable>
  </VCard>
</template>
